/*
 * 戒导航 · 上传模块（jiedh/upload）—— 「点击图片原地放大」浮层
 * =============================================================================
 * 设计依据：doc/输入框富文本编辑器设计方案.md §8.7 二期目标
 *           「把缩略图、点击放大、data-id 全部找回」
 *
 * 【这次补的是什么欠账】
 *   编辑器内的图片 hover 时光标是 zoom-in（放大提示），但点击没有任何反应。
 *   根因链（已实测坐实，不是猜测）：
 *     1) less/forum.less 的 `.Post-body .FoFUpload--Upl-Image-Preview { cursor: zoom-in }`
 *        本意只给「帖子正文」用；
 *     2) 但 fof/rich-text 在 vendor/fof/rich-text/js/src/common/tiptap/TiptapEditorDriver.ts
 *        第 95-96 行把 buildEditorParams 里 push 进来的 classNames（含 Post-body，
 *        见 vendor/fof/rich-text/js/src/common/applyEditor.tsx 第 89 行）
 *        逐个 `classList.add()` 到了 **编辑器的 .ProseMirror 元素本身**，
 *        于是这条「只管正文」的规则连带命中编辑器内的图片；
 *     3) 而编辑器里的 <img> 由本包 UplImagePreview.renderHTML 产出，**刻意不包 <a href>**
 *        （否则编辑正文时点图会直接跳转离开编辑器），全站又没装任何 lightbox 扩展，
 *        也没有任何 click 处理器 —— 提示与行为就此脱节。
 *   正文侧一直是好的：服务端模板 vendor/fof/upload/resources/templates/image-preview.blade.php
 *   渲染了外层 `<a href="{原图}" target="_blank">`，点击会另开标签页看原图。
 *   本文件把编辑器侧缺的那半截实现补上，两端「可放大」的承诺都真正兑现。
 *
 * 【为什么用原生 DOM 浮层，而不用 Flarum 的 Modal 组件】
 *   1. 富文本编辑器可能正身处 Modal 之内（编辑帖子时），再开一个 Modal 要处理模态栈与焦点抢夺；
 *   2. Esc 键有两处宿主会抢先消费：核心 Modal 在 document 上的监听（会顺手关掉编辑弹窗）、
 *      fof/rich-text 的 FlarumShortcuts（Esc → app.composer.close()，见 applyEditor.tsx 第 90 行）。
 *      浮层自身可聚焦、打开时把焦点从编辑器移开，Esc 就只会关闭浮层这一件事；
 *   3. 放大层是一次性的「看图」浮层，不需要 Mithril 的重渲染能力，也不参与 app 的渲染周期。
 *
 * 【层级取值】
 *   z-index 取 var(--zindex-alerts) + 40 = 1100（变量定义见
 *   vendor/flarum/core/less/common/variables.less 第 119-125 行），
 *   高于 composer(1020) / dropdown(1030) / modal-background(1040) / modal(1050) / alerts(1060)。
 */
import app from 'flarum/forum/app';

/** 放大层根节点 class，样式见 less/forum.less 末尾同段。 */
const OVERLAY_CLASS = 'FoFUpload--ImageZoom';

/** 关闭按钮的翻译键；语言包缺词时由 closeLabel() 兜底成中文「关闭」。 */
const CLOSE_KEY = 'jiedh-upload.forum.image_zoom.close_button';
/*
 * ⚠️ 踩过的坑（实测）：Flarum 2.x 的翻译器换成了 ICU MessageFormat
 * （见 vendor/flarum/core/js/src/common/Translator.tsx 第 120-141 行），
 * `trans(key)` 在不传第三个参数时走 `formatter.rich()`，返回的是 **消息片段数组**
 * （例如中文环境下是 `["关闭"]`），不是字符串。
 * 要拿纯文本必须显式传 `extract = true`（第 133 行 `if (extract) return extractText(locale)`），
 * 否则 `typeof text === 'string'` 恒不成立，语言包等于白配、英文环境也会漏出中文兜底。
 */

/** 同时只允许存在一个放大层（模块级单例）。 */
let overlay = null;

/** 打开放大层之前的焦点元素，关闭时把焦点还回去（通常是 .ProseMirror，保证能接着打字）。 */
let restoreFocusTo = null;

/** 关闭按钮文案：优先用语言包，取不到（返回的就是键名本身）时兜底中文。 */
function closeLabel() {
  let text;
  try {
    // 第三个参数 extract=true 是必须的：它让翻译器把 ICU 片段数组压成纯文本（见上方注释）
    text = app.translator.trans(CLOSE_KEY, {}, true);
  } catch {
    text = null;
  }
  return typeof text === 'string' && text && text !== CLOSE_KEY ? text : '关闭';
}

/** 点击放大层任意位置（遮罩 / 图片 / 关闭按钮）都收起：不给「点了没反应」留死角。 */
function handleClick(event) {
  event.stopPropagation();
  closeImageZoom();
}

/**
 * 键盘处理：
 *   · Esc 关闭浮层，并阻断冒泡 —— 否则核心 Modal 的 document 监听会连带关掉「编辑帖子」弹窗；
 *   · 不处理其它按键：浮层被聚焦后编辑器已失焦，PM 的 keymap 收不到事件，不会误触发提交。
 */
function handleKeydown(event) {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  event.stopPropagation();
  closeImageZoom();
}

/** 滚轮停在浮层上时不要让滚动穿透到底层正文。 */
function handleWheel(event) {
  event.preventDefault();
}

/** 收起放大层（未打开时静默返回），并把焦点还给打开前的元素。 */
export function closeImageZoom() {
  if (!overlay) return;
  const el = overlay;
  overlay = null;
  el.removeEventListener('click', handleClick);
  el.removeEventListener('keydown', handleKeydown);
  el.removeEventListener('wheel', handleWheel);
  el.remove();
  const target = restoreFocusTo;
  restoreFocusTo = null;

  // preventScroll：避免聚焦时页面被强行滚回编辑器所在位置
  if (target && target !== document.body) {
    try {
      target.focus({
        preventScroll: true
      });
    } catch {
      /* 元素已被移除等情况下放弃还原即可，绝不让关闭流程抛错 */
    }
  }
}

/**
 * 打开放大层，展示原图。
 *
 * @param src     原图地址（节点属性 url，即服务端 <a href> 里那个地址，不是缩略图）
 * @param caption 说明文字（取节点的 alt / 文件名），为空时不渲染说明行
 */
export function openImageZoom(src, caption) {
  if (!src) return;

  // 连点不同图片时先收起上一层，保证任何时刻只有一个浮层、一套监听
  closeImageZoom();
  const previous = document.activeElement;
  const el = document.createElement('div');
  el.className = OVERLAY_CLASS;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('tabindex', '-1'); // 可编程聚焦（Esc 才有落点），同时不参与 Tab 序列

  const img = document.createElement('img');
  img.className = `${OVERLAY_CLASS}-img`;
  img.src = src;
  img.alt = caption || '';
  el.appendChild(img);
  if (caption) {
    const line = document.createElement('div');
    line.className = `${OVERLAY_CLASS}-caption`;
    line.textContent = caption;
    el.appendChild(line);
  }
  const close = document.createElement('button');
  close.type = 'button';
  close.className = `${OVERLAY_CLASS}-close`;
  close.textContent = '×'; // 通用符号，无需本地化
  close.setAttribute('aria-label', closeLabel());
  close.setAttribute('title', closeLabel());
  el.appendChild(close);
  el.addEventListener('click', handleClick);
  el.addEventListener('keydown', handleKeydown);
  el.addEventListener('wheel', handleWheel, {
    passive: false
  });
  document.body.appendChild(el);
  overlay = el;
  restoreFocusTo = previous;
  el.focus({
    preventScroll: true
  });
}